import { useMemo } from "react";
import { pickemsContest } from "../models/pickems";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { usePickemsMarkets } from "./usePickemsMarkets";
import { parseDate } from "utilities/datetime";
import { useCampaigns } from "../user/useCampaigns";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import type { Always } from "types/utilities";

export const usePickemsContestWithMarkets = (contestId: string) => {
  const {
    contest,
    isLoading: contestLoading,
    error: contestError,
  } = usePickemsContest(contestId);

  const {
    markets,
    isLoading: marketsLoading,
    error: marketsError,
  } = usePickemsMarkets(contestId);

  const contestWithMarkets = useMemo(() => {
    if (!contest) return null;

    return {
      ...contest,
      markets,
    };
  }, [contest, markets]);

  return {
    contest: contestWithMarkets,
    isLoading: contestLoading || marketsLoading,
    error: contestError || marketsError,
  } as const;
};

export const usePickemsContest = (contestId: string) => {
  const canSubmitPickems = useHasPermission("submitPickems");
  const canSubmitPromoCashPickems = useHasPermission(
    "submitPickemsAwardingPromoCash",
  );
  const canSubmitRealCashPickems = useHasPermission(
    "submitPickemsAwardingRealCash",
  );

  const { codes: userCampaigns } = useCampaigns();
  const { data, isLoading, error } = useFirestore(
    contestId && `/pickemsContests/${contestId}`,
    pickemsContest,
    {
      keyPath: "id",
    },
  );

  const transformedContest = useMemo(() => {
    if (!data || !canSubmitPickems) return null;

    // check if the user has the right permission for the contest awarding type
    const isAwardingPromoCash = data.promoCash;
    const isAllowed = isAwardingPromoCash
      ? canSubmitPromoCashPickems
      : canSubmitRealCashPickems;

    if (!isAllowed) {
      return null;
    }

    const hideOnCampaign =
      // 1. if the contest has included campaigns, but none of them match the user's campaigns
      (data.includedCampaignIds &&
        data.includedCampaignIds.length > 0 &&
        !data.includedCampaignIds.some((campaignId) =>
          userCampaigns.includes(campaignId),
        )) ||
      // 2. if the contest has excluded campaigns, and at least one of them matches the user's campaigns
      (data.excludedCampaignIds &&
        data.excludedCampaignIds.length > 0 &&
        data.excludedCampaignIds.some((campaignId) =>
          userCampaigns.includes(campaignId),
        ));

    if (hideOnCampaign) {
      return null;
    }

    const hasCustomPrizePool = data.hasCustomPrizePool;

    return {
      ...data,
      scheduledStartTime: parseDate(data.scheduledStartTime),
      isCustomPrizePool: hasCustomPrizePool,
      prizePoolHeader: hasCustomPrizePool
        ? data.customPrizePoolHeader
        : undefined,
      prizePoolDescription: hasCustomPrizePool
        ? data.customPrizePoolDescription
        : undefined,
      customPrizeTiers: hasCustomPrizePool ? data.customPrizeTiers : undefined,
    };
  }, [data, userCampaigns]);

  return { contest: transformedContest, isLoading, error } as const;
};

export type PickemsContest = Always<
  ReturnType<typeof usePickemsContest>["contest"]
>;
export type PickemsContestWithMarkets = Always<
  ReturnType<typeof usePickemsContestWithMarkets>["contest"]
>;
